/**
 * 戻ったときに送る先（PRODUCT_DEV-1205・10/26 日暮さん「色々な場面で必要になるから共通化」）
 *
 * 一覧から詳細を開いて ← やブラウザの戻るで帰ってきたとき、開いた行まで送るための記憶。
 * 来た画面の記録（`lib/nav/backTracker` の NavEntry）の枠ごとに「送る先」（行を見分ける文字）を1つ持つ。
 * ← もブラウザの戻るも同じ枠へ戻るので、戻ってきたときだけ見つかる（ナビから開き直したときは新しい枠なので無い）。
 *
 * - 一覧の画面は、詳細へ行く直前に `storeReturnAnchor(枠, 送る先)` を呼ぶ
 * - 戻ってきた画面は `takeReturnAnchor(枠)` で1回だけ受け取る（受け取ったら消す。もう一度描き直しても送らない）
 *
 * 形は `lib/mypage/diaryTileMemory.ts` と同じ（sessionStorage・枠 id がキー・古いものから捨てる）。
 * そのタブを開いているあいだだけの記憶なので、アプリを開き直したら送らない。
 */
const KEY = "unizo.nav.returnAnchor";
/** 覚えておく枠の数。古いものから捨てる */
const LIMIT = 30;
/** 送る先の文字の長さ（行の id など。長いものは覚えない） */
export const RETURN_ANCHOR_MAX = 200;

interface Remembered {
  anchor: string;
  at: number;
}

const readAll = (): Record<string, Remembered> => {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, Remembered>) : {};
  } catch {
    return {};
  }
};
const writeAll = (all: Record<string, Remembered>): void => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // 覚えられないだけ（戻ったときに送らない）
  }
};

/** 詳細へ行く直前に呼ぶ。この枠へ戻ってきたときに anchor を返す */
export function storeReturnAnchor(entryId: string | null, anchor: string): void {
  if (!entryId || !anchor || anchor.length > RETURN_ANCHOR_MAX) return;
  const all = readAll();
  all[entryId] = { anchor, at: Date.now() };
  const ids = Object.keys(all);
  if (ids.length > LIMIT) {
    ids
      .sort((a, b) => (all[a].at ?? 0) - (all[b].at ?? 0))
      .slice(0, ids.length - LIMIT)
      .forEach((id) => delete all[id]);
  }
  writeAll(all);
}

/** この枠に送る先があるか（消さない。上へ送る ScrollToTop が、戻ってきた画面では上へ送らないために見る） */
export function hasReturnAnchor(entryId: string | null): boolean {
  if (!entryId) return false;
  const v = readAll()[entryId]?.anchor;
  return typeof v === "string" && v.length > 0;
}

/** この枠の送る先（消さない）。無ければ null */
export function peekReturnAnchor(entryId: string | null): string | null {
  if (!entryId) return null;
  const v = readAll()[entryId]?.anchor;
  return typeof v === "string" && v.length > 0 && v.length <= RETURN_ANCHOR_MAX ? v : null;
}

/** この枠の送る先を1回だけ受け取る（受け取ったら消す）。無ければ null */
export function takeReturnAnchor(entryId: string | null): string | null {
  if (!entryId) return null;
  const all = readAll();
  const v = all[entryId]?.anchor;
  if (all[entryId]) {
    delete all[entryId];
    writeAll(all);
  }
  return typeof v === "string" && v.length > 0 && v.length <= RETURN_ANCHOR_MAX ? v : null;
}
